import * as api from '@gitbook/api-client';
import { getChangeRequestBranchId } from '@gitbook/app-hive';
import type { HiveWorkingBranch, HiveWorkingDocument } from '@gitbook/hive-client';
import { useHiveClient } from '@gitbook/hive-react';
import type { PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { toast } from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useNavigation } from '../../navigation';
import { apiClient, useCurrentOrganizationId } from '../../state';
import { useCopyCommands } from '../Link/useCopyCommands';

/**
 * A group of commands to perform actions on an existing change request (archive, convert to draft).
 */
export function useChangeRequestActionsCommands(props: {
    changeRequest: api.ChangeRequest;
}): () => PaletteGroupDef {
    const { changeRequest } = props;
    const hiveClient = useHiveClient();

    return useEventCallback(() => {
        const isAffectedSync = (sync: HiveWorkingBranch | HiveWorkingDocument) => {
            return (
                sync.contentId === changeRequest.space &&
                sync.branchId === getChangeRequestBranchId(changeRequest.id)
            );
        };

        return {
            key: 'changeRequest.actions',
            commands: [
                changeRequest.status === api.ChangeRequestStatus.Open
                    ? {
                          key: 'changeRequest.draft',
                          icon: Icon.ChangeRequestDraft,
                          title: 'Convert to draft',
                          trigger: async () => {
                              await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                                  await toast.catch(async () => {
                                      await apiClient.changeRequests.updateChangeRequestById(
                                          changeRequest.space,
                                          changeRequest.id,
                                          {
                                              status: 'draft',
                                          }
                                      );

                                      toast('Change request reverted to draft', {
                                          description:
                                              'Continue editing this change request. You can submit it again later.',
                                          icon: Icon.ChangeRequestDraft,
                                      });
                                  }, 'Error while converting change request to draft');
                              }, isAffectedSync);
                          },
                      }
                    : null,
                [api.ChangeRequestStatus.Open, api.ChangeRequestStatus.Draft].includes(
                    changeRequest.status
                )
                    ? {
                          key: 'changeRequest.archive',
                          icon: Icon.Archive,
                          title: 'Archive',
                          trigger: async () => {
                              await toast.catch(async () => {
                                  await hiveClient.sync.withReadOnlyDataSyncs(async () => {
                                      await apiClient.changeRequests.updateChangeRequestById(
                                          changeRequest.space,
                                          changeRequest.id,
                                          {
                                              status: 'archived',
                                          }
                                      );
                                  }, isAffectedSync);

                                  toast('Change request archived', {
                                      description:
                                          'You can reopen this from the change requests panel at any time',
                                      icon: Icon.ChangeRequestArchive,
                                  });
                              }, 'Error while archiving change request');
                          },
                      }
                    : null,
            ].filter((x) => x !== null),
        };
    });
}

export type UseChangeRequestNavigationCommandsProps = {
    space: api.Space | null;
    changeRequest: api.ChangeRequest;
    /**
     * Display open in editor option.
     * @default true
     */
    openInEditor?: boolean;
};

/**
 * A group of commands to navigate on the change request.
 */
export function useChangeRequestNavigationCommands(
    props: UseChangeRequestNavigationCommandsProps
): () => PaletteGroupDef {
    const { changeRequest, space, openInEditor = true } = props;
    const { navigate } = useNavigation();

    return useEventCallback(() => {
        return {
            key: 'changeRequest.navigate',
            commands: [
                space?.permissions.edit && openInEditor
                    ? {
                          key: 'changeRequest.edit',
                          icon: Icon.Edit,
                          title: 'Open in editor',
                          trigger: () => {
                              navigate({
                                  screen: {
                                      type: 'space',
                                      organization: space.organization,
                                      space: changeRequest.space,
                                      changeRequest: changeRequest.id,
                                      mode: 'edit',
                                      sideSheet: undefined,
                                  },
                              });
                          },
                      }
                    : null,
            ].filter((x) => x !== null),
        };
    });
}

export function useChangeRequestCopyCommands(props: {
    changeRequest: api.ChangeRequest;
}): () => PaletteGroupDef {
    const { changeRequest } = props;
    const organizationId = useCurrentOrganizationId();
    const createCopyCommands = useCopyCommands();

    return useEventCallback(() => {
        return {
            key: 'changeRequest.utility',
            commands: [
                createCopyCommands({
                    id: String(changeRequest.number),
                    title: getChangeRequestSubject(changeRequest),
                    target: {
                        screen: {
                            type: 'space',
                            organization: organizationId ?? undefined,
                            space: changeRequest.space,
                            changeRequest: changeRequest.id,
                            sideSheet: undefined,
                        },
                    },
                }),
            ],
        };
    });
}
